import { createEffect, For, Match, mergeProps, Show, Switch } from 'solid-js';
import clsx from 'clsx';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ListProps } from './list-types';
import { TextFontSize } from '../text/text-types';
import { resolveLegacyTextColor } from '../../style/theme';

// Reuse text size class mappings (same as Text component). CVI body sizes
// are identity-mapped to the `.body-*` classes; legacy Atos sizes resolve to
// CVI body tokens internally via their `.text-size-*` classes.
const fontSizeClassMap: Record<TextFontSize, string> = {
    'body-xs': 'body-xs',
    'body-sm': 'body-sm',
    'body-md': 'body-md',
    'body-lg': 'body-lg',
    'body-xl': 'body-xl',

    largest: 'text-size-largest',
    larger: 'text-size-larger',
    large: 'text-size-large',
    normal: 'text-size-normal',
    small: 'text-size-small',
    smaller: 'text-size-smaller',
    footerSmall: 'text-size-footer-small',
    footerHeading: 'text-size-footer-heading',
    largestPDF: 'text-size-largest-pdf',
    largerPDF: 'text-size-larger-pdf',
    largePDF: 'text-size-large-pdf',
    normalPDF: 'text-size-normal-pdf',
    smallPDF: 'text-size-small-pdf',
    smallerPDF: 'text-size-smaller-pdf',
    tinyPDF: 'text-size-tiny-pdf',
};

const defaultProps = {
    fontSize: 'normal' as TextFontSize,
    ordered: false,
};

const baseClasses = [
    // Selector hook only, alongside `atos-text-base` and `heading-common`.
    'atos-list',
    'list-outside',
    'm-0 mt-0 last:mb-0',
    'font-cvi-regular',
    '[&_a]:text-cvi-fg-link [&_a:hover]:underline',
].join(' ');

const bulletClasses = [
    'list-disc',
    'pl-cvi-6',
].join(' ');

const orderedClasses = [
    'list-decimal',
    'pl-cvi-6',
].join(' ');

export const List = (componentProps: ListProps) => {

    const props = mergeProps(defaultProps, componentProps);

    let ref: HTMLUListElement | HTMLOListElement | undefined;
    const setRef = (el: HTMLUListElement | HTMLOListElement) => { ref = el; };

    // This will make sure that any tags with the attachment datatype are opened in a new tab.
    // The main reason behind it is to make sure that files are opened without solidjs
    createEffect(() => {
        ref?.querySelectorAll('[data-type="attachment"]').forEach((atag: any) => {
            atag.target = '_blank';
        });
    });

    const listClasses = () => clsx(
        baseClasses,
        fontSizeClassMap[props.fontSize],
        props.ordered ? orderedClasses : bulletClasses,
    );

    const listItems = () => (
        <Switch>
            <Match when={props.items}>
                <For each={props.items}>{ (item: string) => {
                    return item && (
                        <li
                            innerHTML={item}
                            style={{ color: props?.colorText?.color }}
                            class='pb-cvi-6'
                        />
                    );
                }}</For>
            </Match>
            <Match when={props.children}>
                <For each={props.children}>{ (child: string) => {
                    return child && <li style={{ color: props?.colorText?.color }}>{child}</li>;
                }}</For>
            </Match>
        </Switch>
    );

    return (
        <ErrorCatcher componentName='List'>
            <Show when={props.items && props.items?.length > 0} fallback={<></>}>
                {props.ordered ? (
                    <ol ref={setRef} class={listClasses()}>
                        {listItems()}
                    </ol>
                ) : (
                    <ul ref={setRef} class={listClasses()}>
                        {listItems()}
                    </ul>
                )}
            </Show>
        </ErrorCatcher>
    );
};

List.parseProps = (atts: any) => {
    let parsedListItems: any = [];

    if (Array.isArray(atts.content)) {
        parsedListItems = atts?.content?.map((item: any) => {
            if (!item || !item.innerHTML) {
                return '';
            }
            let parsedItem = item.innerHTML;
            parsedItem = parsedItem.replace('\n<li>', '');
            parsedItem = parsedItem.replace('</li>\n', '');
            return parsedItem;
        });
    }

    const resolvedHex = resolveLegacyTextColor(atts.colorText);
    const colorText = resolvedHex
        ? { color: resolvedHex, name: atts.colorText?.name ?? 'fgDefault' }
        : undefined;

    return {
        items: parsedListItems,
        fontSize: atts.fontSize || 'normal',
        ordered: atts.ordered ?? false,
        colorText,
    };
};
